import { useCallback, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useProfile, candidateSections, candidateUpdate } from '@/hooks/use-profile'
import {
  countSkillEvidenceReferences,
  filterOutMatchingClaimedSkill,
  formatSkillCascadeImpactMessage,
  matchesProfileSkillName,
  prepareAppendClaimedSkill,
  prepareRenameClaimedSkill,
  resolveCanonicalClaimedSkill,
} from '@/lib/profile-evidence-display'
import { buildUnifiedSkillItems, orderSkillItemsWithSessionPins, skillItemDisplayName, unifiedSkillItemKey } from '@/utils/profile-skills-display'
import {
  ArtifactCanvasBody,
  ArtifactCanvasFooterBar,
  ArtifactCanvasDismissAction,
} from '@/components/artifact/artifact-canvas-layout'

import { LinkSkillEvidenceDialog } from './link-skill-evidence-dialog'
import { SkillManagerAddRow, SkillManagerSkillRow, type SkillEvidenceLinkType } from './skill-manager-row'

type LinkEvidenceTarget = {
  skillName: string
  type: SkillEvidenceLinkType
}

const SKILL_ROW_ENTER_MS = 220

function normalizeSkillSessionKey(name: string): string {
  return name.trim().toLowerCase()
}

function pinSkillForSession(names: string[], skillName: string): string[] {
  const key = normalizeSkillSessionKey(skillName)
  return [skillName, ...names.filter((name) => normalizeSkillSessionKey(name) !== key)]
}

function unpinSkillFromSession(names: string[], skillName: string): string[] {
  const key = normalizeSkillSessionKey(skillName)
  return names.filter((name) => normalizeSkillSessionKey(name) !== key)
}

function renamePinnedSkillInSession(names: string[], fromName: string, toName: string): string[] {
  const fromKey = normalizeSkillSessionKey(fromName)
  return names.map((name) => (normalizeSkillSessionKey(name) === fromKey ? toName : name))
}

export function SkillsManagerCanvas({
  onClose,
  focusAdd = false,
}: {
  onClose: () => void
  focusAdd?: boolean
}) {
  const { profile, updateProfile, isUpdating } = useProfile()
  const candidate = candidateSections(profile)
  const [addDraft, setAddDraft] = useState('')
  const [addError, setAddError] = useState<string | null>(null)
  const [editingKey, setEditingKey] = useState<string | null>(null)
  const [editDraft, setEditDraft] = useState('')
  const [editError, setEditError] = useState<string | null>(null)
  const [removeTarget, setRemoveTarget] = useState<string | null>(null)
  const [removeError, setRemoveError] = useState<string | null>(null)
  const [linkEvidenceTarget, setLinkEvidenceTarget] = useState<LinkEvidenceTarget | null>(null)
  const [sessionPinnedSkills, setSessionPinnedSkills] = useState<string[]>([])
  const [enteringSkillKeys, setEnteringSkillKeys] = useState<Set<string>>(() => new Set())

  const skillItems = useMemo(() => {
    const items = buildUnifiedSkillItems(candidate.skills, candidate.skill_summaries)
    return orderSkillItemsWithSessionPins(items, sessionPinnedSkills)
  }, [candidate.skills, candidate.skill_summaries, sessionPinnedSkills])

  const profileSkills = candidate.skills ?? []

  const editExcludeNames = useMemo(() => {
    if (!editingKey) return profileSkills
    return profileSkills.filter((skill) => !matchesProfileSkillName(editingKey, skill))
  }, [editingKey, profileSkills])

  const removeLabel = useMemo(() => {
    if (!removeTarget) return 'this skill'
    return resolveCanonicalClaimedSkill(candidate.skills, removeTarget)?.trim() || removeTarget.trim()
  }, [removeTarget, candidate.skills])

  const removeCounts = useMemo(
    () => countSkillEvidenceReferences(candidate, removeLabel),
    [candidate, removeLabel]
  )

  const editHint = useMemo(() => {
    if (!editingKey) return null
    const label = resolveCanonicalClaimedSkill(candidate.skills, editingKey)?.trim() || editingKey.trim()
    const counts = countSkillEvidenceReferences(candidate, label)
    const total = counts.experiences + counts.education + counts.certifications
    if (total === 0) return null
    return formatSkillCascadeImpactMessage(label, counts, 'rename')
  }, [editingKey, candidate])

  const cancelEditing = useCallback(() => {
    setEditingKey(null)
    setEditDraft('')
    setEditError(null)
  }, [])

  const startEditing = useCallback(
    (skillKey: string) => {
      const canonical = resolveCanonicalClaimedSkill(candidate.skills, skillKey) ?? skillKey
      setEditingKey(skillKey)
      setEditDraft(canonical)
      setEditError(null)
      setAddError(null)
    },
    [candidate.skills]
  )

  const handleAddSkill = useCallback(
    (rawName: string) => {
      setAddError(null)
      if (!profile) return

      const prep = prepareAppendClaimedSkill(candidate.skills, rawName)
      if (!prep.ok) {
        setAddError(prep.error)
        return
      }

      updateProfile(
        candidateUpdate({ skills: prep.skills }),
        {
          onSuccess: () => {
            setAddDraft('')
            const addedName = prep.trimmed
            setSessionPinnedSkills((prev) => pinSkillForSession(prev, addedName))
            const enterKey = normalizeSkillSessionKey(addedName)
            setEnteringSkillKeys((prev) => new Set(prev).add(enterKey))
            window.setTimeout(() => {
              setEnteringSkillKeys((prev) => {
                if (!prev.has(enterKey)) return prev
                const next = new Set(prev)
                next.delete(enterKey)
                return next
              })
            }, SKILL_ROW_ENTER_MS)
          },
          onError: (err: unknown) => {
            setAddError(err instanceof Error ? err.message : 'Could not add skill')
          },
        }
      )
    },
    [profile, candidate.skills, updateProfile]
  )

  const handleDone = useCallback(() => {
    onClose()
  }, [onClose])

  const handleSaveEdit = useCallback(
    (rawName: string) => {
      setEditError(null)
      if (!profile || !editingKey) return

      const prep = prepareRenameClaimedSkill(candidate.skills, editingKey, rawName)
      if (!prep.ok) {
        setEditError(prep.error === 'No changes to save.' ? null : prep.error)
        if (prep.error === 'No changes to save.') {
          cancelEditing()
        }
        return
      }

      updateProfile(
        candidateUpdate({ skills: prep.skills }),
        {
          onSuccess: () => {
            setSessionPinnedSkills((prev) => renamePinnedSkillInSession(prev, editingKey, prep.trimmed))
            cancelEditing()
          },
          onError: (err: unknown) => {
            setEditError(err instanceof Error ? err.message : 'Could not rename skill')
          },
        }
      )
    },
    [profile, candidate.skills, editingKey, cancelEditing, updateProfile]
  )

  const handleConfirmRemove = useCallback(() => {
    if (!profile || !removeTarget) return
    setRemoveError(null)

    const nextSkills = filterOutMatchingClaimedSkill(candidate.skills, removeTarget)
    updateProfile(
      candidateUpdate({ skills: nextSkills }),
      {
        onSuccess: () => {
          setRemoveTarget(null)
          setSessionPinnedSkills((prev) => unpinSkillFromSession(prev, removeTarget))
          if (editingKey && matchesProfileSkillName(editingKey, removeTarget)) {
            cancelEditing()
          }
        },
        onError: (err: unknown) => {
          setRemoveError(err instanceof Error ? err.message : 'Could not remove skill')
        },
      }
    )
  }, [profile, candidate.skills, removeTarget, updateProfile, editingKey, cancelEditing])

  const openLinkEvidence = useCallback(
    (skillKey: string, type: SkillEvidenceLinkType) => {
      const canonical =
        resolveCanonicalClaimedSkill(candidate.skills, skillKey)?.trim() || skillKey.trim()
      cancelEditing()
      setLinkEvidenceTarget({ skillName: canonical, type })
    },
    [candidate.skills, cancelEditing]
  )

  return (
    <>
      <ArtifactCanvasBody className="flex h-full min-h-0 flex-col space-y-0 p-0">
        <div className="min-h-0 flex-1 overflow-y-auto p-6">
          <ul className="space-y-2">
            <SkillManagerAddRow
              inputId="skills-manager-add"
              draft={addDraft}
              error={addError}
              disabled={isUpdating}
              autoFocus={focusAdd}
              excludeNames={profileSkills}
              onDraftChange={(next) => {
                setAddDraft(next)
                setAddError(null)
              }}
              onAdd={handleAddSkill}
            />
            {skillItems.map((item) => {
              const skillKey = skillItemDisplayName(item)
              const itemKey = unifiedSkillItemKey(item)
              const isEditing = editingKey != null && matchesProfileSkillName(editingKey, skillKey)
              const animateEntrance = enteringSkillKeys.has(normalizeSkillSessionKey(skillKey))

              return (
                <SkillManagerSkillRow
                  key={itemKey}
                  item={item}
                  skillKey={skillKey}
                  isEditing={isEditing}
                  editDraft={editDraft}
                  editError={editError}
                  editHint={isEditing ? editHint : null}
                  editExcludeNames={editExcludeNames}
                  disabled={isUpdating}
                  animateEntrance={animateEntrance}
                  onStartEdit={() => startEditing(skillKey)}
                  onEditDraftChange={(next) => {
                    setEditDraft(next)
                    setEditError(null)
                  }}
                  onSaveEdit={handleSaveEdit}
                  onCancelEdit={cancelEditing}
                  onLinkExperience={() => openLinkEvidence(skillKey, 'experience')}
                  onLinkEducation={() => openLinkEvidence(skillKey, 'education')}
                  onLinkCertification={() => openLinkEvidence(skillKey, 'certification')}
                  onRemove={() => {
                    setRemoveError(null)
                    setRemoveTarget(skillKey)
                  }}
                />
              )
            })}
          </ul>
        </div>

        <ArtifactCanvasFooterBar
          footerClassName="shrink-0"
          actions={<ArtifactCanvasDismissAction onClose={handleDone} disabled={isUpdating} />}
        />
      </ArtifactCanvasBody>

      {typeof document !== 'undefined' && linkEvidenceTarget
        ? createPortal(
            <LinkSkillEvidenceDialog
              open
              onOpenChange={(open) => {
                if (!open) setLinkEvidenceTarget(null)
              }}
              skillName={linkEvidenceTarget.skillName}
              type={linkEvidenceTarget.type}
            />,
            document.body
          )
        : null}

      <AlertDialog open={removeTarget != null} onOpenChange={(open) => !open && setRemoveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove skill</AlertDialogTitle>
            <AlertDialogDescription>
              {formatSkillCascadeImpactMessage(removeLabel, removeCounts, 'remove')}
              {removeError ? (
                <span className="mt-2 block text-sm text-destructive">{removeError}</span>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isUpdating}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isUpdating}
              onClick={(event) => {
                event.preventDefault()
                handleConfirmRemove()
              }}
            >
              {isUpdating ? 'Removing…' : 'Remove'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
